/**
 * Magnetic Button Markup Transformer
 *
 * Transforms existing elements into the magnetic button markup structure
 * while PRESERVING inner content (like animated chars).
 *
 * Usage:
 * ------
 * import { prepareMagneticButtons } from './magnetic-markup.js';
 * import MagneticButtons from './magnetic-buttons.js';
 *
 * // Wrapper with link inside (heading, div, etc.)
 * prepareMagneticButtons('.portfolio-card__heading', { type: 'link' });
 *
 * // Button wrapper with link inside - adds fill effect
 * prepareMagneticButtons('.wp-block-button', { type: 'button' });
 *
 * // Standalone anchor (no wrapper)
 * prepareMagneticButtons('.wp-block-read-more', { type: 'link' });
 *
 * // Standalone button (no wrapper)
 * prepareMagneticButtons('.wp-block-navigation-submenu__toggle', { type: 'link' });
 *
 * // Then initialize magnetic behavior
 * new MagneticButtons();
 */

/**
 * Transforms a single element into magnetic button markup
 *
 * @param {HTMLElement} element - The element (wrapper, anchor, or button)
 * @param {Object} config - Configuration options
 * @param {string} config.type - 'link' (no fill) or 'button' (with fill)
 * @param {number} config.strength - Magnet strength (default: 50)
 * @param {number} config.strengthText - Text element strength (default: 25)
 * @returns {HTMLElement} The transformed element
 */
function prepareMagneticButton(element, config = {}) {
	const defaults = {
		type: 'button',
		strength: 50,
		strengthText: 25,
	};

	const settings = { ...defaults, ...config };

	// Check if the element itself is an anchor (standalone link)
	if (element.tagName === 'A') {
		return transformStandaloneAnchor(element, settings);
	}

	// Check if the element itself is a button (standalone button)
	if (element.tagName === 'BUTTON') {
		return transformStandaloneButton(element, settings);
	}

	// Otherwise, find the anchor inside the wrapper
	const anchor = element.querySelector('a');
	if (!anchor) {
		console.warn('prepareMagneticButton: No anchor found in', element);
		return element;
	}

	// Add .btn to the wrapper
	element.classList.add('btn');

	// Add magnetic classes and data attributes to the anchor
	anchor.classList.add('btn-click', 'magnetic');
	anchor.dataset.strength = settings.strength;
	anchor.dataset.strengthText = settings.strengthText;

	// Store the current inner HTML of the anchor
	const innerContent = anchor.innerHTML;

	// Build the new structure based on type
	if (settings.type === 'button') {
		// Button type: include btn-fill
		anchor.innerHTML = `
			<div class="btn-fill"></div>
			<div class="btn-text">
				<span class="btn-text-inner">${innerContent}</span>
			</div>
		`;
	} else {
		// Link type: no fill, just wrap content
		anchor.innerHTML = `
			<span class="btn-text">
				<span class="btn-text-inner">${innerContent}</span>
			</span>
		`;
	}

	return element;
}

/**
 * Transforms a standalone anchor element
 */
function transformStandaloneAnchor(anchor, settings) {
	// Add magnetic classes to the anchor itself
	anchor.classList.add('btn-click', 'magnetic');
	anchor.dataset.strength = settings.strength;
	anchor.dataset.strengthText = settings.strengthText;

	// Store the current inner HTML
	const innerContent = anchor.innerHTML;

	// Build the new structure based on type
	if (settings.type === 'button') {
		// Button type: include btn-fill
		anchor.innerHTML = `
			<div class="btn-fill"></div>
			<div class="btn-text">
				<span class="btn-text-inner">${innerContent}</span>
			</div>
		`;
	} else {
		// Link type: no fill, just wrap content
		anchor.innerHTML = `
			<span class="btn-text">
				<span class="btn-text-inner">${innerContent}</span>
			</span>
		`;
	}

	return anchor;
}

/**
 * Transforms a standalone button element
 */
function transformStandaloneButton(button, settings) {
	// Add magnetic classes to the button itself
	button.classList.add('btn-click', 'magnetic');
	button.dataset.strength = settings.strength;
	button.dataset.strengthText = settings.strengthText;

	// Store the current inner HTML
	const innerContent = button.innerHTML;

	// Build the new structure based on type
	if (settings.type === 'button') {
		// Button type: include btn-fill
		button.innerHTML = `
			<div class="btn-fill"></div>
			<div class="btn-text">
				<span class="btn-text-inner">${innerContent}</span>
			</div>
		`;
	} else {
		// Link type: no fill, just wrap content
		button.innerHTML = `
			<span class="btn-text">
				<span class="btn-text-inner">${innerContent}</span>
			</span>
		`;
	}

	return button;
}

/**
 * Transforms multiple wrapper elements into magnetic button markup
 *
 * @param {string} selector - CSS selector for wrapper elements
 * @param {Object} config - Configuration options (see prepareMagneticButton)
 * @returns {HTMLElement[]} Array of transformed elements
 */
function prepareMagneticButtons(selector, config = {}) {
	const elements = document.querySelectorAll(selector);
	return Array.from(elements).map((el) => prepareMagneticButton(el, config));
}

export { prepareMagneticButton, prepareMagneticButtons };
